/**
 * Bot implementation is a compute-only engine that can choose a next move
 * for a given game state + player. It does not decide *when* to act and it
 * does not submit moves (that belongs in `deps/room/*`).
 */
import { signal } from '@preact/signals-react'
import type { Bot, BotChooseMoveParams, BotDeps } from './bot.types'

export function makeBot(deps: BotDeps): Bot {
  const progressMessages = signal<string[]>([])
  const percentComplete = signal<number>(0)
  const isProcessing = signal<boolean>(false)
  let aiProcessingRef: Promise<any | null> | null = null
  let activeRun: { cancelled: boolean } | null = null

  async function chooseMove(params: BotChooseMoveParams): Promise<any | null> {
    // Don't process if already processing
    if (aiProcessingRef) {
      return null
    }

    const { gameId, gameState, ctx, bgioPlayerId, botType, botConfig, gameVersion } = params

    // Don't process if game is over / missing inputs
    if (!gameId || !ctx || ctx?.gameover) {
      progressMessages.value = []
      percentComplete.value = 0
      isProcessing.value = false
      return null
    }

    let botFactory
    let gamePlugin
    if (gameVersion) {
      const getBotFactory = deps.gameRegistry.getBotFactoryAtVersion
      const getGamePlugin = deps.gameRegistry.loadGamePluginAtVersion
      if (!getBotFactory || !getGamePlugin) return null
      ;[botFactory, gamePlugin] = await Promise.all([
        getBotFactory(gameId, gameVersion),
        getGamePlugin(gameId, gameVersion),
      ])
    } else {
      if (!deps.gameRegistry.supportsBots(gameId)) return null
      botFactory = deps.gameRegistry.getBotFactory(gameId)
      gamePlugin = deps.gameRegistry.getGamePlugin(gameId)
    }

    if (!botFactory) return null
    if (!gamePlugin?.isBgio || !gamePlugin.bgioGameDef) {
      return null
    }

    // Mark as processing
    const run = { cancelled: false }
    activeRun = run
    isProcessing.value = true
    const processing = (async (): Promise<any | null> => {
      try {
        // Clear previous progress
        progressMessages.value = []
        percentComplete.value = 0

        // Add a small delay to make AI moves feel more natural (200ms - 500ms)
        const delay = Math.random() * 300 + 200
        await new Promise(resolve => setTimeout(resolve, delay))

        // Check again if cancelled / game is over (might have ended during delay)
        if (run.cancelled || ctx?.gameover) {
          progressMessages.value = []
          percentComplete.value = 0
          isProcessing.value = false
          return null
        }

        // Create bot for this player
        const bot = botFactory(
          gamePlugin.bgioGameDef,
          botType || 'mcts',
          botConfig
        )

        // Start the generator
        const generator = bot.chooseNextMove(
          gameState,
          ctx,
          bgioPlayerId
        )

        // Process generator yields
        let move: any = null
        for await (const progress of generator) {
          // Check if cancelled
          if (run.cancelled) {
            break
          }

          // Check if game ended while processing
          if (ctx?.gameover) {
            console.log('[bot] Game ended during AI processing, aborting move')
            break
          }

          // Update progress
          if (progress.percentComplete !== undefined) {
            percentComplete.value = progress.percentComplete
          }

          // Update progress messages
          if (progress.message) {
            const currentMessages = progressMessages.value
            progressMessages.value = [...currentMessages, progress.message]
          }

          // If we have a result, use it
          if (progress.result) {
            move = progress.result
            break
          }
        }

        // Check if game ended before submitting move
        if (ctx?.gameover) {
          console.log('[bot] Game ended before AI move could be computed')
          progressMessages.value = []
          percentComplete.value = 0
          isProcessing.value = false
          return null
        }

        // Get the final return value from the generator
        // Note: async generators don't expose return value directly, so we rely on progress.result
        // If move wasn't set from progress.result, try to get it from the last yield
        if (!move) {
          // The generator should have yielded the result in the last progress update
          // If not, we'll need to handle this differently
          console.error('[bot] Bot did not return a move')
          progressMessages.value = []
          percentComplete.value = 0
          isProcessing.value = false
          return null
        }

        // Final check before submitting move
        if (run.cancelled) {
          console.log('[bot] AI move was cancelled')
          progressMessages.value = []
          percentComplete.value = 0
          isProcessing.value = false
          return null
        }
        
        if (ctx?.gameover) {
          console.log('[bot] Game ended right before returning AI move')
          progressMessages.value = []
          percentComplete.value = 0
          isProcessing.value = false
          return null
        }

        return move
      } catch (error: any) {
        console.error('[bot] Error generating AI move:', error)
        progressMessages.value = []
        percentComplete.value = 0
        return null
      } finally {
        if (activeRun === run) {
          aiProcessingRef = null
          activeRun = null
          isProcessing.value = false
        }
      }
    })()
    aiProcessingRef = processing

    return await processing
  }

  function cancel() {
    // Keep the run's token alive so its asynchronous checks observe cancellation.
    if (activeRun) {
      activeRun.cancelled = true
    }
    aiProcessingRef = null
    activeRun = null
    isProcessing.value = false
    progressMessages.value = []
    percentComplete.value = 0
  }

  return {
    progressMessages,
    percentComplete,
    isProcessing,
    chooseMove,
    cancel
  }
}
